import { useState } from "react";
import type { EnvironmentRole } from "@starsach/shared-types";
import { useChain, useEnvironmentSettings, useGrantRole, useMemberships, useReorderChain, useRevokeRole } from "../../api/hooks";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, RelativeTime, StatusPill, btnPrimary } from "../../components/ui";
import { useStepUp } from "../../auth/StepUpProvider";

/**
 * Who may do what, and where (requirements.md section 10.7, ADR-0008).
 * Reading is open to anyone holding a role here; every change is
 * manager-only, and the API refuses regardless of what this screen shows.
 */
export function AccessTab({ environmentId }: { environmentId: string }) {
  const memberships = useMemberships(environmentId);
  if (memberships.isPending) return <LoadingState label="Loading access" />;
  if (memberships.isError) return <ErrorState error={memberships.error} onRetry={() => void memberships.refetch()} />;

  return (
    <div className="space-y-4">
      <Roster environmentId={environmentId} />
      <Chain environmentId={environmentId} />
    </div>
  );
}

function Roster({ environmentId }: { environmentId: string }) {
  const q = useMemberships(environmentId);
  const grantRole = useGrantRole(environmentId);
  const revokeRole = useRevokeRole(environmentId);
  const { ensureFresh } = useStepUp();
  // Section 13.3: access is a security change. The API refuses a stale sign-in with STEP_UP_REQUIRED; asking first is the polite half.
  const grant = { ...grantRole, mutate: (v: Parameters<typeof grantRole.mutate>[0]) => void ensureFresh().then(() => grantRole.mutate(v), () => undefined) };
  const revoke = { ...revokeRole, mutate: (v: Parameters<typeof revokeRole.mutate>[0]) => void ensureFresh().then(() => revokeRole.mutate(v), () => undefined) };
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<EnvironmentRole>("admin");
  const data = q.data;
  if (!data) return null;

  const error = grant.error ?? revoke.error;

  return (
    <Card title="Who has access here">
      <p className="mb-2 text-sm text-neutral-600 dark:text-neutral-400">
        A role is held per environment, so full authority here says nothing about anywhere else. An admin authors and sets
        overrides; a manager also approves changes and defines the chain.
      </p>
      {error && <ErrorState error={error} />}
      <DataTable
        caption="Everyone holding a role on this environment"
        rows={data.memberships}
        rowKey={(m) => m.userId}

        sortBy={(m) => m.email ?? m.userId}
        empty={<EmptyState title="Nobody holds a role here yet" />}
        columns={[
          { key: "who", header: "User", render: (m) => m.email ?? m.userId },
          { key: "role", header: "Role", render: (m) => <StatusPill status={m.role} tone={m.role === "manager" ? "ok" : "neutral"} /> },
          { key: "seen", header: "Last signed in", render: (m) => (m.lastSeenAt ? <RelativeTime iso={m.lastSeenAt} /> : <StatusPill status="never" tone="warning" />) },
          { key: "granted", header: "Granted", render: (m) => <>by {m.grantedBy}, <RelativeTime iso={m.grantedAt} /></> },
          {
            key: "actions",
            header: "",
            render: (m) => (
              <span className="flex gap-2">
                <button
                  type="button"
                  className="text-sm underline disabled:opacity-50"
                  disabled={grant.isPending}
                  onClick={() => grant.mutate({ userId: m.userId, role: m.role === "manager" ? "admin" : "manager" })}
                >
                  Make {m.role === "manager" ? "admin" : "manager"}
                </button>
                <button
                  type="button"
                  className="text-sm text-red-700 underline disabled:opacity-50 dark:text-red-400"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate({ userId: m.userId })}
                >
                  Revoke
                </button>
              </span>
            ),
          },
        ]}
      />
      {data.managerCount <= 1 && (
        <p role="status" className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          One manager holds this environment. Removing or demoting them would leave nobody able to grant a role back, so
          it is refused until a second manager exists.
        </p>
      )}

      <form
        className="mt-4 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (userId) grant.mutate({ userId, role });
          setUserId("");
        }}
      >
        <label className="text-sm">
          Grant to
          <select
            className="mt-1 block rounded border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">Choose someone</option>
            {data.candidates.map((c) => (
              <option key={c.userId} value={c.userId}>{c.email ?? c.userId}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          As
          <select
            className="mt-1 block rounded border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
            value={role}
            onChange={(e) => setRole(e.target.value as EnvironmentRole)}
          >
            <option value="admin">admin</option>
            <option value="manager">manager</option>
          </select>
        </label>
        <button type="submit" className={btnPrimary} disabled={!userId || grant.isPending}>
          Grant
        </button>
      </form>
    </Card>
  );
}

function Chain({ environmentId }: { environmentId: string }) {
  const q = useChain(environmentId);
  const reorder = useReorderChain(environmentId);
  const settings = useEnvironmentSettings(environmentId);
  if (!q.data) return null;
  const chain = q.data.chain;

  const move = (index: number, by: -1 | 1) => {
    const ids = chain.map((c) => c.environmentId);
    const target = index + by;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorder.mutate({ environmentIds: ids });
  };

  return (
    <Card title={`${q.data.name}: the environment chain`}>
      <p className="mb-2 text-sm text-neutral-600 dark:text-neutral-400">
        Promotion runs upward. Whether an environment is live, and whether changes into it need approval, are declared
        here rather than inferred from where it sits - a sandbox may legitimately sit above production.
      </p>
      {(reorder.error ?? settings.error) && <ErrorState error={reorder.error ?? settings.error} />}
      <ol aria-label="Environment chain, lowest first" className="space-y-2">
        {chain.map((c, i) => (
          <li key={c.environmentId} className="flex flex-wrap items-center gap-3 rounded border border-neutral-200 p-3 dark:border-neutral-800">
            <span className="text-xs text-neutral-500">{i + 1}</span>
            <span className="font-medium">{c.friendlyName}</span>
            {c.live && <StatusPill status="live" tone="critical" />}
            {c.changesRequireApproval && <StatusPill status="approval required" tone="warning" />}
            {c.breakGlassEnabled && <StatusPill status="break glass enabled" tone="neutral" />}
            <span className="ml-auto flex gap-2">
              <button type="button" className="text-sm underline disabled:opacity-40" disabled={i === 0 || reorder.isPending} onClick={() => move(i, -1)}>
                Move up
              </button>
              <button type="button" className="text-sm underline disabled:opacity-40" disabled={i === chain.length - 1 || reorder.isPending} onClick={() => move(i, 1)}>
                Move down
              </button>
            </span>
            {c.environmentId === environmentId && (
              <div className="flex w-full flex-wrap gap-4 border-t border-neutral-200 pt-2 text-sm dark:border-neutral-800">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={c.live} disabled={settings.isPending} onChange={(e) => settings.mutate({ live: e.target.checked })} />
                  Real callers reach this environment
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={c.changesRequireApproval} disabled={settings.isPending} onChange={(e) => settings.mutate({ changesRequireApproval: e.target.checked })} />
                  Changes need a manager's approval
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={c.breakGlassEnabled} disabled={settings.isPending} onChange={(e) => settings.mutate({ breakGlassEnabled: e.target.checked })} />
                  Allow break glass
                </label>
              </div>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}
